<template>
  <div class="min-h-screen">
    <BaseContainer>
      <main class="max-w-2xl mx-auto py-16 md:py-24">
        <p v-if="data.event?.info1" class="font-semibold text-text mb-3">
          {{ data.event.info1 }}
        </p>
        <h1 class="h2 mb-4">
          {{ title }}
        </h1>
        <p class="text-text mb-10">
          {{ description }}
        </p>
        <div class="flex flex-col sm:flex-row gap-4">
          <BaseButton v-if="canJoinWaitlist" color="primary" @click="showWaitlistModal = true">
            {{ t('event.subscribe_to_waitlist') }}
          </BaseButton>
          <BaseButton :color="canJoinWaitlist ? 'outline' : 'primary'" @click="close">
            {{ t('common.close') }}
          </BaseButton>
        </div>
      </main>
    </BaseContainer>

    <WaitlistModal
      v-if="waitlistEvent"
      v-model="showWaitlistModal"
      :event="waitlistEvent"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  import WaitlistModal from '@/components/event/WaitlistModal.vue';
  import BaseButton from '@/components/ui/BaseButton.vue';
  import BaseContainer from '@/components/ui/BaseContainer.vue';
  import type { CheckoutEventState } from '@/types';
  import { closeEmbedDrawer } from '@/utils/embedNavigation';
  import { formatDate } from '@/utils/formatDate';

  const props = defineProps<{
    data: CheckoutEventState;
  }>();

  const { locale, t } = useI18n();
  const router = useRouter();
  const showWaitlistModal = ref(false);

  const title = computed(() => t(`checkout.event_state.${props.data.state}.title`));
  const description = computed(() => {
    if (props.data.state === 'not_started' && props.data.salestart) {
      return t('checkout.event_state.not_started.description_with_date', {
        date: formatDate(props.data.salestart, {
          locale: locale.value,
          showTime: true,
          timezone: props.data.timezone,
        }),
      });
    }
    return t(`checkout.event_state.${props.data.state}.description`);
  });
  const canJoinWaitlist = computed(() =>
    (props.data.state === 'ended' || props.data.state === 'sold_out') &&
    Boolean(props.data.waitlists?.length)
  );
  const waitlistEvent = computed(() => canJoinWaitlist.value ? {
    id: props.data.id,
    waitlists: props.data.waitlists!,
  } : null);

  function close() {
    if (!closeEmbedDrawer()) router.push('/');
  }
</script>